"use client"

import { useState } from "react"

const InfoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const ChevronUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
)

const LightbulbIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-yellow-500"
  >
    <line x1="9" y1="18" x2="15" y2="18" />
    <line x1="10" y1="22" x2="14" y2="22" />
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
  </svg>
)

const InfoTooltip = ({ text }: { text: string }) => {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <div className="relative inline-block">
      <button
        className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <InfoIcon />
      </button>
      {showTooltip && (
        <div className="absolute z-50 whitespace-normal max-w-[300px] min-w-[240px] p-3 text-sm bg-white rounded-lg shadow-lg border border-gray-200 right-full top-0 -translate-x-2">
          <div className="absolute top-2 -right-1 w-2 h-2 bg-white transform rotate-45 border-t border-r border-gray-200" />
          <p className="text-gray-700 break-words">{text}</p>
        </div>
      )}
    </div>
  )
}

const SketchNote = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white border-2 border-gray-300 p-4 rounded-lg transform shadow-md ${className}`}>{children}</div>
)

const ActivityCard = ({ title, description }: { title: string; description: string }) => (
  <div className="bg-white p-3 rounded-lg text-sm flex-1 hover:bg-green-100 transition-colors min-w-[120px]">
    <div className="flex items-center">
      <strong>{title}</strong>
    </div>
    <p className="text-xs text-gray-600">{description}</p>
  </div>
)

export default function ProcessIntro() {
  const [showOverview, setShowOverview] = useState(false)

  const sprintPhases = [
    {
      name: "Map",
      icon: "🗺️",
      color: "bg-pink-100",
      tooltip: "Weten wij ons probleem duidelijk genoeg? Meer onderzoek nodig?",
    },
    {
      name: "Schets",
      icon: "✏️",
      color: "bg-yellow-100",
      tooltip: "Weten wij hoe wij kunnen het oplossen?",
    },
    {
      name: "Beslis",
      icon: "🎯",
      color: "bg-green-100",
      tooltip: "Weten wij welke oplossing is best? Meer onderzoek nodig?",
    },
    {
      name: "Prototype",
      icon: "🎨",
      color: "bg-blue-100",
      tooltip: "Hebben wij het probleem opgelost?",
    },
    {
      name: "Test",
      icon: "🧪",
      color: "bg-purple-100",
      tooltip: "Hebben wij het probleem opgelost?",
    },
  ]

  return (
    <div className="w-full mb-8">
      <button
        onClick={() => setShowOverview(!showOverview)}
        className="w-full bg-white p-4 rounded-lg shadow mb-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-700 flex items-center gap-2">
          <LightbulbIcon />
          {showOverview ? "Verberg procesoverzicht" : "Bekijk procesoverzicht"}
        </span>
        {showOverview ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </button>

      {showOverview && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="overflow-x-auto">
            <div className="min-w-[1400px]">
              <div className="flex gap-6">
                {/* Fase 1: Probleemdefinitie */}
                <div className="flex-1 bg-green-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">👥</span>
                    </div>
                    <h3 className="font-bold text-xl mb-2">
                      Probleemdefinitie
                      <InfoTooltip text="Hoe kunnen wij meer weten over ons probleem?" />
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">Probleem helder krijgen</p>
                    <div className="flex gap-2 flex-wrap justify-center">
                      <span className="bg-blue-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        🔍 Analyseren
                      </span>
                      <span className="bg-green-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        💡 Adviseren
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3 no-wrap">
                    <ActivityCard title="Probleem- verkenning" description="Bepaal de kern van het probleem" />
                    <ActivityCard title="Aannames verzamelen" description="Inzichten ophalen" />
                    <ActivityCard title="Aannames mapping" description="Prioriteer risico's" />
                    <ActivityCard title="Hypothese statement" description="Maak tests" />
                  </div>
                </div>

                {/* Fase 2: Design Sprint */}
                <div className="flex-1 bg-blue-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">🎨</span>
                    </div>
                    <h3 className="font-bold text-xl mb-2">Design Sprint</h3>
                    <p className="text-gray-600 text-sm mb-3">Van idee naar prototype</p>
                    <div className="flex gap-2 flex-wrap justify-center">
                      <span className="bg-yellow-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        ✏️ Ontwerpen
                      </span>
                      <span className="bg-purple-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        🛠️ Realiseren
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-nowrap gap-4 overflow-x-auto pb-4">
                    {sprintPhases.map((phase) => (
                      <div
                        key={phase.name}
                        className={`${phase.color} p-4 rounded-lg text-center flex-shrink-0 flex flex-col items-center`}
                        style={{
                          width: phase.name === "Map" ? "320px" : "220px",
                        }}
                      >
                        <span className="font-bold whitespace-nowrap mb-4">
                          {phase.icon} {phase.name}
                          <InfoTooltip text={phase.tooltip} />
                        </span>

                        {phase.name === "Map" && (
                          <div className="grid grid-cols-2 gap-2 w-full">
                            <ActivityCard title="Prototype Persona's" description="Beschrijf de gebruikers" />
                            <ActivityCard title="Journey Map" description="Breng proces in kaart" />
                          </div>
                        )}

                        {phase.name === "Schets" && (
                          <div className="w-full">
                            <ActivityCard title="Crazy 8's" description="Genereer ideeën" />
                          </div>
                        )}

                        {phase.name === "Beslis" && (
                          <div className="w-full">
                            <ActivityCard title="Impact Matrix" description="Prioriteer oplossingen" />
                          </div>
                        )}

                        {phase.name === "Prototype" && (
                          <div className="w-full">
                            <ActivityCard title="Storyboard & Prototype" description="Bouw klikbaar prototype" />
                          </div>
                        )}

                        {phase.name === "Test" && (
                          <div className="w-full">
                            <ActivityCard title="Gebruikerstest" description="Test prototype met 5 gebruikers" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Key Principles */}
                  <div className="bg-white/90 p-4 rounded-lg shadow-sm mt-6">
                    <h4 className="text-center font-bold mb-4">Kernprincipes</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <SketchNote>
                        <div className="font-medium">⏱️ Timebox Alles</div>
                        <div className="text-sm text-gray-600">Houd tempo vast</div>
                      </SketchNote>
                      <SketchNote>
                        <div className="font-medium">💡 Eerst Zelf Denken</div>
                        <div className="text-sm text-gray-600">Combineer eigen ideeën</div>
                      </SketchNote>
                      <SketchNote>
                        <div className="font-medium">🔄 Iteratief Proces</div>
                        <div className="text-sm text-gray-600">Test en verbeter continu</div>
                      </SketchNote>
                      <SketchNote>
                        <div className="font-medium">👥 Gebruikersgericht</div>
                        <div className="text-sm text-gray-600">Focus op echte behoeftes</div>
                      </SketchNote>
                      <SketchNote>
                        <div className="font-medium">🚀 Snel Leren</div>
                        <div className="text-sm text-gray-600">Fail fast, learn faster</div>
                      </SketchNote>
                    </div>
                  </div>
                </div>

                {/* Fase 3: Afronding */}
                <div className="flex-1 bg-purple-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📊</span>
                    </div>
                    <h3 className="font-bold text-xl mb-2">Afronding</h3>
                    <p className="text-gray-600 text-sm mb-3">Plan de volgende stappen</p>
                    <div className="flex gap-2 flex-wrap justify-center">
                      <span className="bg-red-100 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                        📊 Manage & Control
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3 no-wrap">
                    <div className="bg-white p-3 rounded-lg text-sm flex-1 hover:bg-purple-100 transition-color min-w-[120px]">
                      <strong>Product Visie</strong>
                      <p className="text-xs text-gray-600">Bepaal richting</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-sm flex-1 hover:bg-purple-100 transition-colors min-w-[120px]">
                      <strong>User Story Mapping</strong>
                      <p className="text-xs text-gray-600">Breng features in kaart</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-sm flex-1 hover:bg-purple-100 transition-colors min-w-[120px]">
                      <strong>Backlog & Planning</strong>
                      <p className="text-xs text-gray-600">Prioriteer werk</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-sm flex-1 hover:bg-purple-100 transition-colors min-w-[120px]">
                      <strong>Documentatie</strong>
                      <p className="text-xs text-gray-600">Leg lessen vast</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
